<template>
  <div :class="field.classes()" v-click-outside="onClickOutside">
    <div :class="field.classes('label', true)">
      {{label}} <span class="required-icon" v-if="required">*</span>
    </div>
    <errors v-if="error.length" :error="error"></errors>

    <div :class="[field.classes('body'),{'dropdown--open':dropDownOpen}]">
      <div :class="field.classes('selected-item')"
           @click="toggleDropDown">
        <div v-if="value" :class="field.classes('selected-item__value')">
          <component v-if="fieldOptions.selectedItemComponent" :is="fieldOptions.selectedItemComponent"
                     :model="currentModel" :value="value" v-bind="fieldOptions.selectedItemComponentOptions"></component>
          <template v-else>{{(currentModel && currentModel.nameForSelect()) || value}}</template>
        </div>
        <div v-else
             :class="field.classes('placeholder')">
          {{placeholder || label}}
        </div>
        <ui-inline-icon
          v-if="!fieldReadOnly"
          icon="arrow"
          direction="down"
          :class="field.classes('selected-item__arrow')"></ui-inline-icon>
      </div>

      <div :class="field.classes('dropdown')">
        <input v-if="fieldOptions.search"
               ref="searchInput"
               v-model="search"
               :placeholder="$t('labels.search')"
               :class="field.classes('dropdown__search')"
               @keydown.down.prevent="nextResult"
               @keydown.up.prevent="previousResult"
               @keydown.tab.prevent="nextResult"
        />

        <div :class="field.classes('dropdown__emptyText')" v-if="!search && !searchResult.length">{{emptyText}}</div>

        <div :class="field.classes('dropdown__searchResult')" v-else>
          <div v-if="searchResult.length===0">{{ $t('labels.nothing_founded') }}</div>
          <div v-else>
            <div :class="valueClasses(item.getId())"
                 ref="searchResult"
                 v-for="(item,index) in searchResult"
                 :tabindex="index"
                 :key="item.getId()"
                 @click="!valueDisabled(item.getId()) && setItem(item)"
                 @keydown.down.prevent="nextResult"
                 @keydown.up.prevent="previousResult"
                 @keydown.tab.prevent="nextResult"
                 @keydown.enter.prevent="!valueDisabled(item.getId()) && setItem(item)"
            >
              {{item.nameForSelect()}}
            </div>
          </div>
        </div>

        <template v-if="search && searchResult && fieldOptions.create">
          <div v-if="!createFormOpen" :class="field.classes('dropdown__createBtn')" @click="openCreateForm">
            {{fieldOptions.createText || `+ ${$t('labels.create')}`}}
          </div>
          <div v-else :class="field.classes('createForm')">
            <active-form
              small
              @created="onCreated"
              cancel
              @cancel="onCancel"
              :form="fieldOptions.model.createForm"></active-form>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import {fieldProps} from '../BZCore/ActiveField'
  import Errors from '../errors.vue'
  import UiInlineIcon from 'src/components/Ui/Icon/InlineIcon.vue'

  export default {
    name: 'ModelSelectField',
    props: fieldProps,
    components: {Errors, UiInlineIcon},
    data () {
      return {
        search: '',
        createFormOpen: false,
        dropDownOpen: false,
        searchResult: false,
        searchTimeout: 0,
        currentModel: null,
        currentActive: -1
      }
    },
    created () {
      if ((this.value === null || this.value === undefined) && this.multiSelect) {
        this.$emit('input', [])
      }
      if (this.defaultValue) {
        this.$emit('input', this.defaultValue)
      }
      this.loadModel()
      this.preFind()
    },
    watch: {
      value () {
        this.loadModel()
      },
      search () {
        this.planSearch()
      }
    },
    methods: {
      previousResult () {
        if (this.allResultsDisabled) {
          return
        }
        if (this.$refs.searchResult && this.$refs.searchResult[this.currentActive - 1]) {
          this.currentActive--
          this.$refs.searchResult[this.currentActive].focus()
        } else if (this.$refs.searchResult) {
          this.currentActive = this.$refs.searchResult.length - 1
          this.$refs.searchResult[this.currentActive].focus()
        }
        if (this.disabledValues.indexOf(this.searchResult[this.currentActive].getId()) !== -1) {
          this.previousResult()
        }
      },
      nextResult () {
        if (this.allResultsDisabled) {
          return
        }
        if (this.$refs.searchResult && this.$refs.searchResult[this.currentActive + 1]) {
          this.currentActive++
          this.$refs.searchResult[this.currentActive].focus()
        } else if (this.$refs.searchResult) {
          this.currentActive = 0
          this.$refs.searchResult[this.currentActive].focus()
        }
        if (this.disabledValues.indexOf(this.searchResult[this.currentActive].getId()) !== -1) {
          this.nextResult()
        }
      },
      onCancel () {
        this.createFormOpen = false
      },
      valueClasses (value) {
        const classes = []
        classes.push(this.field.classes('dropdown__searchResult__item'))

        if (this.valueDisabled(value)) {
          classes.push(this.field.classes('dropdown__searchResult__item--disabled'))
        }
        return classes
      },
      valueDisabled (value) {
        return this.disabledValues.indexOf(value) !== -1
      },
      async loadModel () {
        if (this.currentModel && this.currentModel.getId() !== this.value) {
          this.currentModel = null
        }
        if (this.value) {
          const newModel = await this.fieldOptions.model.load(this.value)
          if (newModel && newModel.getId() === this.value) {
            this.currentModel = newModel
          }
        }
      },
      async preFind () {
        if (this.fieldOptions.preFind !== undefined && this.fieldOptions.preFind !== false) {
          this.searchResult = await this.finder(this.fieldOptions.preFind, this.scope, this.rootScope, this.formScope)
          this.currentActive = -1
        }
      },
      openCreateForm () {
        this.createFormOpen = true
      },
      onClickOutside () {
        this.dropDownOpen = false
        this.createFormOpen = false
      },
      toggleDropDown () {
        if (!this.fieldReadOnly) {
          this.dropDownOpen = !this.dropDownOpen
          if (this.dropDownOpen) {
            this.$refs.searchInput.focus()
          }
        }
        this.search = ''
        this.createFormOpen = false
      },
      onCreated (item) {
        this.setItem(item)
        this.createFormOpen = false
      },
      setItem (item) {
        this.search = ''
        this.dropDownOpen = false
        this.currentModel = item
        this.$emit('input', item.getId())
      },
      planSearch () {
        clearTimeout(this.searchTimeout)
        if (this.search) {
          this.searchTimeout = setTimeout(async () => {
            const search = this.search
            this.searchResult = await this.finder(search, this.scope, this.rootScope, this.formScope)
            this.currentActive = -1
          }, 600)
        } else {
          this.searchResult = false
          this.preFind()
        }
      }
    },
    computed: {
      allResultsDisabled () {
        if (this.disabledValues.length === 0) {
          return false
        }
        for (const result of this.searchResult) {
          if (this.disabledValues.indexOf(result.getId()) === -1) {
            return false
          }
        }
        return true
      },
      disabledValues () {
        if (this.fieldOptions.disabledHandler) {
          return this.fieldOptions.disabledHandler.apply(this.field, [this.searchResult, this.scope])
        }
        return this.fieldOptions.disabledValues || []
      },
      finder () {
        return this.fieldOptions.find || this.fieldOptions.model.find
      },
      fieldReadOnly () {
        return this.readOnly === true || this.fieldOptions.readOnly || false
      },
      multiSelect () {
        return !!this.fieldOptions.multiSelect
      },
      emptyText () {
        return this.fieldOptions.emptyText || this.$t('labels.enter_name')
      },
      errorMessage () {
        const text = []
        for (const error of this.error) {
          text.push(error.message)
        }
        return text.join(', ')
      }
    }
  }
</script>

<style lang="scss">
  .active-field--model-select {
    position: relative;

    &__createForm {
      border-top: 1px solid #E4E7ED;
      padding: 10px 11px 0;
      margin: 0 -11px;

      .active-form__content {
        background: transparent;
        padding: 0;
      }

      .active-field--string__label {
        display: none;
      }

      .active-field--phone__label {
        display: none;
      }

      .active-field--select__label {
        display: none;
      }

      .active-field {
        margin-bottom: 10px;
      }

      .active-form__submit {
        height: 28px;

        background: #2F80ED;
        border-radius: 4px;
        width: 100%;
        padding: 6px 20px;
      }

      .active-form__footer {
        border-top: 1px solid #E4E7ED;
        padding: 10px 11px 0;
        margin: 0 -11px;
      }
    }

    &__selected-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      background: #FDFDFD;
      border: 1px solid #dcdfe6;
      min-height: 40px;
      border-radius: 4px;
      padding: 0 15px;
      cursor: pointer;
      color: #303133;

      &__value {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      &__arrow {
        padding-left: 12px;
        flex-shrink: 0;
      }
    }

    &__placeholder {
      color: #909399;
      font-size: 14px;
    }

    &__dropdown {
      opacity: 0;
      pointer-events: none;
      transition: opacity .2s ease;

      position: absolute;
      width: 100%;
      margin-top: 10px;
      background: #FDFDFD;
      border: 1px solid #E3E6EC;
      box-sizing: border-box;
      box-shadow: 0px 5px 25px rgba(0, 0, 0, 0.08);
      border-radius: 2px;
      z-index: 30;
      padding: 11px;

      &__createBtn {
        border-top: 1px solid #E4E7ED;;
        height: 30px;
        display: flex;
        align-items: center;
        text-align: center;
        justify-content: center;
        margin: 0 -11px -11px;
        font-size: 14px;
        cursor: pointer;
        color: #2F80ED;
      }

      &__search {
        background: #EBEEF5;
        border-radius: 2px;
        border: 0;
        display: block;
        width: 100%;
        font-size: 12px;
        color: #303133;
        height: 21px;
        padding: 0 10px;
        margin-bottom: 8px;
      }

      &__emptyText {
        font-size: 14px;
        color: #606266;
        padding: 8px 0;
      }

      &__searchResult {
        &__item {
          font-size: 14px;
          color: #303133;
          cursor: pointer;
          padding: 7px 11px;
          margin: 0 -11px;

          &.active, &:focus, &:hover:not(&--disabled) {
            outline: 0;
            color: #2F80ED;
            background: #EBEEF5;
          }

          &--disabled {
            opacity: 0.6;
            cursor: default;
          }
        }
      }
    }

    &__body {
      &.dropdown--open {
        .active-field--model-select__dropdown {
          opacity: 1;
          pointer-events: auto;
        }
      }
    }
  }
</style>
